/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {Layers, TombstoneRipLayers} from "../../../view/TombstoneRipLayers";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BigWinParticles} from "./BigWinParticles";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap, TimelineLite} from "gsap";
import {TombstoneRip} from "../../../TombstoneRip";
import {SpineHelper} from "../../../../core/utils/SpineHelper";
import {TombstoneRipGameMode} from "../../../gameflow/TombstoneRipGameMode";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";

export class BigWinView extends PIXI.Container {

    private _layer:StageLayer;
    private _particles:BigWinParticles;
    private _dimmer:PIXI.Graphics;
    private _redDimmer:PIXI.Graphics;
    private _photoContainer:PIXI.Container;
    private _currentPhoto:PIXI.spine.Spine | null;
    private _effectsNormal:PIXI.spine.Spine | null;
    private _effectsBlend:PIXI.spine.Spine | null;
    private _effectContainer:PIXI.Container;
    private _textContainer:PIXI.Container;
    private _textNormal:PIXI.spine.Spine | null;
    private _textBlend:PIXI.spine.Spine | null;
    private _dust:PIXI.spine.Spine;
    private _underEffectContainer:PIXI.Container;
    private _underEffect:PIXI.spine.Spine | null;

    private _isLandscape:boolean;
    private _isPlaying:boolean = false;

    constructor() {
        super();
        this._layer = TombstoneRipLayers.getLayer(Layers.BIG_WIN)!;
        this.position.set(360, 360);
        this._layer.addChild(this);

        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this.addChild(this._dimmer);

        const redDimmerContainer = new PIXI.Sprite();
        redDimmerContainer.blendMode = PIXI.BLEND_MODES.ADD;
        this.addChild(redDimmerContainer);

        this._redDimmer = new PIXI.Graphics();
        this._redDimmer.alpha = 0;
        redDimmerContainer.addChild(this._redDimmer);

        this._underEffectContainer = new PIXI.Container();
        this.addChild(this._underEffectContainer);

        this._photoContainer = new PIXI.Container();
        this.addChild(this._photoContainer);

        this._dust = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("bigWinDust"));
        this._dust.autoUpdate = true;
        this._dust.visible = false;
        this.addChild(this._dust);

        this._effectContainer = new PIXI.Container();
        this.addChild(this._effectContainer);

        this._textContainer = new PIXI.Container();
        this.addChild(this._textContainer);

        this._particles = new BigWinParticles();
        this.addChild(this._particles);
    }

    public onOrientationChanged():void {
        if(!this._isPlaying) {
            return;
        }

        if(this._isLandscape === NolimitApplication.isLandscape) {
            return;
        }

        this._isLandscape = NolimitApplication.isLandscape;

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 1);
        this._dimmer.drawRect(-NolimitApplication.screenBounds.width * 0.5, -NolimitApplication.screenBounds.height * 0.5, NolimitApplication.screenBounds.width, NolimitApplication.screenBounds.height);
        this._dimmer.endFill();

        this._redDimmer.clear();
        this._redDimmer.beginFill(0x380000, 1);
        this._redDimmer.drawRect(-NolimitApplication.screenBounds.width * 0.5, -NolimitApplication.screenBounds.height * 0.5, NolimitApplication.screenBounds.width, NolimitApplication.screenBounds.height);
        this._redDimmer.endFill();

        this._particles.resize();
    }

    public start():void {
        this._dust.visible = true;
        this._dust.state.setAnimation(0, "animation", true);
    }

    public getLevelAnimation(level:number, duration:number, isEndLevel:boolean):TimelineLite {
        this._isPlaying = true;
        this.onOrientationChanged();

        if(!this._textNormal) {
            this._textNormal = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("bigWinTextNormal"));
            this._textNormal.autoUpdate = true;
            this._textNormal.y = 230;
            this._textNormal.visible = false;
            this._textContainer.addChild(this._textNormal);
        }
        if(!this._textBlend) {
            this._textBlend = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("bigWinTextBlend"));
            this._textBlend.autoUpdate = true;
            this._textBlend.y = 230;
            this._textBlend.visible = false;
            this._textContainer.addChild(this._textBlend);
        }

        const tl = new TimelineLite();
        tl.add([
            gsap.to(this._dimmer, {alpha : 0.6, duration : 0.4}),
            () => {
                this.resetPhoto();
                this.resetEffects();
                this._particles.play(level);
                this._currentPhoto = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset(`bigWinPhoto${level + 1}`));
                this._currentPhoto.autoUpdate = true;
                this._photoContainer.addChild(this._currentPhoto);
                const currentName = (!!SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfxUS && level > 1) ? `P${level + 1}_US_animation` : `P${level + 1}`;
                this._currentPhoto.state.setAnimation(0, currentName, false);
                this._textNormal!.visible = true;
                this._textNormal!.state.setAnimation(0, `t0${level + 1}`, false);
                this._textBlend!.visible = true;
                this._textBlend!.state.setAnimation(0, `g0${level + 1}`, false);
                this._effectsNormal = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset(`bigWinL${level + 1}Normal`));
                this._effectsNormal.autoUpdate = true;
                this._effectContainer.addChild(this._effectsNormal);
                this._effectsNormal.state.setAnimation(0, "animation", false);
                this._effectsBlend = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset(`bigWinL${level + 1}Blend`));
                this._effectsBlend.autoUpdate = true;
                this._effectContainer.addChild(this._effectsBlend);
                this._effectsBlend.state.setAnimation(0, "animation", false);
                if(level > 1) {
                    // play under effects
                    this._underEffect = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset(`bigWinUnderL${level + 1}`));
                    this._underEffect.autoUpdate = true;
                    this._underEffectContainer.addChild(this._underEffect);
                    this._underEffect.state.setAnimation(0, "under", false);
                    if(TombstoneRip.model.parsedGameData.mode === TombstoneRipGameMode.FREESPIN_SUPER) {
                        gsap.to(TombstoneRip.background.foregroundFs_Super, {alpha : 0, duration : 0.2});
                    }
                }
            }
        ]);

        if(level === 3) {
            tl.add(gsap.to(this._redDimmer, {alpha : 0.4, duration : 0.4}), 0);
        }
        if(level === 4) {
            tl.add(gsap.to(this._redDimmer, {alpha : 0.6, duration : 0.4}), 0);
        }

        if(!isEndLevel) {
            const endingDuration = this._textNormal!.spineData.findAnimation(`t0${level + 1}end`).duration;
            tl.add(() => {
                this._textNormal!.visible = true;
                this._textNormal!.state.setAnimation(0, `t0${level + 1}end`, false);
                this._textBlend!.visible = false;
            }, duration - endingDuration);
        }


        return tl;
    }

    private resetPhoto():void {
        if(this._currentPhoto) {
            this._currentPhoto.state.clearTrack(0);
            this._currentPhoto.skeleton.setToSetupPose();
            this._currentPhoto.state.setEmptyAnimation(0, 0);
            if(this._currentPhoto.parent) {
                this._currentPhoto.parent.removeChild(this._currentPhoto);
            }
            this._currentPhoto.destroy();
        }
        this._currentPhoto = null;
    }

    private resetEffects():void {
        if(this._effectsNormal) {
            SpineHelper.destroySpine(this._effectsNormal);
            this._effectsNormal = null;
        }

        if(this._effectsBlend) {
            SpineHelper.destroySpine(this._effectsBlend);
            this._effectsBlend = null;
        }

        if(this._underEffect) {
            SpineHelper.destroySpine(this._underEffect);
            this._underEffect = null;
        }
    }

    public playEnding(endLevel:number):void {
        this._isPlaying = false;
        const tl = gsap.timeline();
        tl.add([
            () => {
                if(this._currentPhoto) {
                    const entryName = (!!SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfxUS && endLevel > 1) ? `P${endLevel + 1}_US_animation` : `P${endLevel + 1}`;
                    const entry = this._currentPhoto.state.setAnimation(0, entryName, false);
                    entry.trackTime = entry.trackEnd;
                    gsap.to(this._currentPhoto, {
                        alpha : 0, duration : 0.6, onComplete : () => {
                            if(!this._isPlaying) {
                                this.resetPhoto();
                            }
                        }
                    });
                }
                this._textNormal!.visible = true;
                this._textNormal!.state.setAnimation(0, `t0${endLevel + 1}end`, false);
                SpineHelper.destroySpine(this._textBlend!);
                this._textBlend = null;

                this._particles.stop();
                this.resetEffects();
                if(TombstoneRip.model.parsedGameData.mode === TombstoneRipGameMode.FREESPIN_SUPER) {
                    gsap.to(TombstoneRip.background.foregroundFs_Super, {alpha : 1, duration : 0.2});
                }
            },
            gsap.to(this._dimmer, {alpha : 0, duration : 0.2}),
            gsap.to(this._redDimmer, {alpha : 0, duration : 0.2}),
            gsap.to(this._dust, {
                alpha : 0, duration : 0.3, onComplete : () => {
                    this._dust.state.clearTrack(0);
                    this._dust.skeleton.setToSetupPose();
                    this._dust.state.setEmptyAnimation(0, 0);
                    this._dust.visible = false;
                }
            }),

            gsap.to(this, {
                duration : this._textNormal?.spineData.findAnimation(`t0${endLevel + 1}end`).duration,
                onComplete : () => {
                    if(!this._isPlaying) {
                        SpineHelper.destroySpine(this._textNormal!);
                        this._textNormal = null;
                    }
                }
            })
        ]);
    }
}